/* VESSARIQ interaction layer. Never intercept navigation, submit, or cart state. */
(() => {
  'use strict';
  if (window.VessariqLiquid) return;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const narrow = matchMedia('(max-width: 700px)');
  const controls = 'a[href],button,summary,[role="button"],[role="tab"],input[type="radio"],input[type="checkbox"],input[type="submit"],input[type="button"],select';
  const effects = new Set(), presses = new Map(), depths = new Set(), visible = new Set();
  let config, frame = 0, scanFrame = 0;
  const enabled = key => config?.dataset[key] !== 'false';
  const clearEffects = () => { effects.forEach(n => n.remove()); effects.clear(); presses.forEach(a => a.cancel()); presses.clear(); };
  function controlFrom(event) {
    for (const node of event.composedPath()) {
      if (!(node instanceof Element)) continue;
      const found = node.matches(controls) ? node : node.closest(controls);
      if (found) return found;
      const label = node.closest('label');
      if (label?.control?.matches('input[type="radio"],input[type="checkbox"]')) return label.control;
    }
    return null;
  }
  function surfaceFor(control) {
    if (control.matches('input[type="radio"],input[type="checkbox"]')) {
      const label = control.closest('label') || control.labels?.[0];
      return label?.querySelector(':scope > span') || label || control;
    }
    return control;
  }
  function feedback(event) {
    if (reduce.matches || !enabled('liquid') || document.hidden) return;
    const control = controlFrom(event);
    if (!control || control.matches(':disabled,[aria-disabled="true"]') || control.closest('[inert],[data-vq-no-liquid]')) return;
    const target = surfaceFor(control), rect = target.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2 || !target.animate) return;
    // Detached fixed-position paint: no markup inside inputs, no text/image distortion.
    const layer = document.createElement('span');
    layer.className = 'vq-liquid-paint'; layer.setAttribute('aria-hidden', 'true');
    const centre = event.type !== 'pointerdown' || !Number.isFinite(event.clientX);
    const x = centre ? rect.width / 2 : Math.min(rect.width, Math.max(0, event.clientX - rect.left));
    const y = centre ? rect.height / 2 : Math.min(rect.height, Math.max(0, event.clientY - rect.top));
    let background = target, dark = false;
    while (background instanceof Element) {
      const rgba = getComputedStyle(background).backgroundColor.match(/[\d.]+/g)?.map(Number);
      if (rgba && (rgba.length < 4 || rgba[3] > .65)) { dark = rgba[0] * .2126 + rgba[1] * .7152 + rgba[2] * .0722 < 140; break; }
      background = background.parentElement;
    }
    layer.dataset.tone = dark ? 'light' : 'dark';
    Object.assign(layer.style, { left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px`, height: `${rect.height}px`, borderRadius: getComputedStyle(target).borderRadius });
    const size = Math.min(720, Math.max(80, Math.hypot(rect.width, rect.height) * 1.4));
    for (let index = 0; index < 2; index++) {
      const drop = document.createElement('i');
      Object.assign(drop.style, { left: `${x}px`, top: `${y}px`, width: `${size}px`, height: `${size}px` });
      layer.append(drop);
      drop.animate([
        { transform: 'translate(-50%,-50%) scale(.035,.08) rotate(-18deg)', borderRadius: '35% 65% 70% 30% / 45% 38% 62% 55%', opacity: .9 },
        { transform: 'translate(-50%,-50%) scale(.68,.48) rotate(9deg)', borderRadius: '65% 35% 42% 58% / 36% 62% 38% 64%', opacity: .8, offset: .42 },
        { transform: 'translate(-50%,-50%) scale(1.12,.86) rotate(20deg)', borderRadius: '44% 56% 63% 37% / 60% 40% 60% 40%', opacity: 0 }
      ], { duration: 520 + index * 100, delay: index * 35, easing: 'cubic-bezier(.22,.7,.16,1)', fill: 'both' });
    }
    // Open dialogs live in the browser top layer: attach paint there, not underneath.
    (control.closest('dialog[open]') || document.body).append(layer);
    effects.add(layer);
    while (effects.size > 10) { const oldest = effects.values().next().value; oldest.remove(); effects.delete(oldest); }
    setTimeout(() => { layer.remove(); effects.delete(layer); }, 800);
    if (!target.querySelector('img,video,svg.vq-hero-mark') && !target.matches('select,input')) {
      presses.get(target)?.cancel();
      const animation = target.animate([{ scale: '1' }, { scale: '.982', offset: .35 }, { scale: '1' }], { duration: 240, easing: 'cubic-bezier(.22,.7,.2,1)' });
      presses.set(target, animation);
      const clean = () => { if (presses.get(target) === animation) presses.delete(target); };
      animation.onfinish = clean; animation.oncancel = clean;
    }
  }
  const depthSelector = '.vq-pathway,.vq-home-lookbook,.vq-education,[data-vq-parallax]';
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => { if (entry.isIntersecting) visible.add(entry.target); else visible.delete(entry.target); }); schedule();
  }, { rootMargin: '80px' }) : null;
  function draw() {
    frame = 0;
    const writes = [];
    depths.forEach(node => {
      if (!node.isConnected) { depths.delete(node); visible.delete(node); observer?.unobserve(node); return; }
      if (reduce.matches || !enabled('parallax') || document.hidden || node.contains(document.activeElement)) { writes.push([node, 0]); return; }
      if (observer && !visible.has(node)) return;
      const rect = node.getBoundingClientRect();
      const progress = Math.max(-1, Math.min(1, (rect.top + rect.height / 2 - innerHeight / 2) / Math.max(1, innerHeight)));
      writes.push([node, progress * (narrow.matches ? -14 : -28)]);
    });
    writes.forEach(([node, y]) => node.style.setProperty('--vq-depth-y', `${y.toFixed(2)}px`));
  }
  function schedule() { if (!frame) frame = requestAnimationFrame(draw); }
  function scan() {
    scanFrame = 0;
    config = document.querySelector('[data-vq-experience]');
    document.querySelectorAll(depthSelector).forEach(node => {
      if (depths.has(node) || node.closest('dialog,form,.vnp-gallery,[data-vq-no-parallax]')) return;
      depths.add(node); node.classList.add('vq-depth-panel');
      const layer = document.createElement('span'); layer.className = 'vq-depth-layer'; layer.setAttribute('aria-hidden', 'true'); node.prepend(layer);
      if (observer) observer.observe(node); else visible.add(node);
    });
    const replay = document.querySelector('[data-vq-replay-opening]');
    if (replay) replay.hidden = !document.querySelector('[data-vq-hero-film] .vq-hero-mark');
    schedule();
  }
  function refresh() { if (!scanFrame) scanFrame = requestAnimationFrame(scan); }
  document.addEventListener('pointerdown', event => { if (event.button === 0 && event.isPrimary !== false) feedback(event); }, { passive: true });
  document.addEventListener('click', event => {
    if (event.detail === 0) feedback(event);
    if (event.target.closest?.('[data-vq-replay-opening]')) window.VessariqEntry?.mount(true);
  }, { passive: true });
  document.addEventListener('change', event => { if (event.target.matches?.('select')) feedback(event); }, { passive: true });
  addEventListener('scroll', () => { clearEffects(); schedule(); }, { passive: true });
  addEventListener('resize', schedule, { passive: true });
  document.addEventListener('focusin', schedule);
  document.addEventListener('visibilitychange', () => { if (document.hidden) clearEffects(); schedule(); });
  reduce.addEventListener('change', () => { clearEffects(); schedule(); });
  narrow.addEventListener('change', schedule);
  document.addEventListener('shopify:section:load', event => {
    if (event.target.querySelector?.('[data-vq-experience]')) window.VessariqEntrySetup?.();
    refresh();
  });
  document.addEventListener('shopify:section:unload', refresh);
  addEventListener('pageshow', () => { clearEffects(); refresh(); });
  const changes = new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes].some(node => node instanceof Element && (node.matches(depthSelector) || node.querySelector(depthSelector))))) refresh();
  });
  function init() { scan(); changes.observe(document.body, { childList: true, subtree: true }); }
  window.VessariqLiquid = { refresh, clear: clearEffects };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
})();
